<script setup lang="ts">
import { sortBy } from 'lodash'
import { computed, ref } from 'vue'

import {
  EventsLineupsParticipant,
  Participant,
  SubParticipantPosition,
} from '@collector/sportsapi-client-legacy'
import Select from '@mobile/components/Select/Select.vue'
import { isHomeTeam } from '@mobile/reusables/entityUtils'
import { useEventInjections } from '@mobile/views/Relation/Shared/RelationDependencies/useEventInjections'

import { getFilteredSelectOptions } from '../../Table/partcipantsSearchFilter'
import SelectGroup from './SelectGroup.vue'

interface Props {
  name: string
  participant: Participant
  prioritizeGoalkeepers?: boolean
  filterByPosition?: SubParticipantPosition
  important?: boolean
  isFulfilled?: boolean
  nullable?: boolean
  disabled?: boolean
}

const props = defineProps<Props>()

// composables
const { probableHomeLineups, probableAwayLineups } = useEventInjections()

// model
const model = defineModel<InstanceType<typeof Select>['$props']['value']>()

// state
const searchValue = ref('')
const lineupsParticipants = isHomeTeam(props.participant)
  ? probableHomeLineups
  : probableAwayLineups

// computed
const filteredSelectOptions = computed(() =>
  getFilteredSelectOptions(
    sortParticipants(lineupsParticipants.value),
    searchValue.value,
  ),
)

// methods
function sortParticipants(
  participants: EventsLineupsParticipant[],
): EventsLineupsParticipant[] {
  let filteredParticipants = participants

  // Filter for specific position if the prop is set
  if (props.filterByPosition) {
    filteredParticipants = participants.filter(
      (participant) =>
        participant.participant_position === props.filterByPosition,
    )
  }

  if (props.prioritizeGoalkeepers) {
    return sortBy(filteredParticipants, [
      (participant) => participant.participant_name !== 'None',
      (participant) =>
        participant.participant_position !== SubParticipantPosition.Goalkeeper,
      (participant) => participant.participant_shirt_number,
    ])
  }

  return sortBy(filteredParticipants, [
    (participant) => participant.participant_name !== 'None',
    (participant) => participant.participant_shirt_number,
  ])
}
</script>

<template>
  <div class="mb-3">
    <SelectGroup
      v-model="model"
      :options="filteredSelectOptions"
      :name
      :important
      :isFulfilled
      :nullable
      :disabled
      trackBy="name"
      @searchChange="searchValue = $event"
    />
  </div>
</template>
